import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Edit, Trash2, PlusCircle, ExternalLink, Search, Mail, FileText, Users } from "lucide-react";

const AdminDashboard = () => {
    const { authAxios } = useAuth();
    const [articles, setArticles] = useState([]);
    const [subs, setSubs] = useState({ total: 0, active: 0 });
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");

    const load = () => {
        setLoading(true);
        Promise.all([
            authAxios.get("/admin/articles"),
            authAxios.get("/admin/newsletter/subscribers"),
        ])
            .then(([a, s]) => {
                setArticles(a.data);
                setSubs({ total: s.data.total, active: s.data.active });
            })
            .catch(() => toast.error("Erro ao carregar dados"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDelete = async (slug, title) => {
        if (
            !window.confirm(
                `Tem a certeza que quer eliminar "${title}"? Esta ação não pode ser desfeita.`,
            )
        )
            return;
        try {
            await authAxios.delete(`/admin/articles/${slug}`);
            toast.success("Artigo eliminado");
            load();
        } catch {
            toast.error("Erro ao eliminar");
        }
    };

    const filtered = articles.filter(
        (a) =>
            a.title.toLowerCase().includes(q.toLowerCase()) ||
            a.author.toLowerCase().includes(q.toLowerCase()) ||
            a.category.toLowerCase().includes(q.toLowerCase()),
    );

    return (
        <div data-testid="admin-dashboard">
            {/* Stats cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <div className="bg-white rounded-xl p-6 border border-gray-100 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-[var(--acd-blue-600)]/10 text-[var(--acd-blue-600)] flex items-center justify-center">
                        <FileText size={22} />
                    </div>
                    <div>
                        <div className="text-3xl font-bold text-[var(--acd-navy-900)]">
                            {articles.length}
                        </div>
                        <div className="text-xs text-[color:var(--acd-muted)] uppercase tracking-widest">
                            Artigos publicados
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-xl p-6 border border-gray-100 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-[var(--acd-orange)]/10 text-[var(--acd-orange)] flex items-center justify-center">
                        <Users size={22} />
                    </div>
                    <div>
                        <div className="text-3xl font-bold text-[var(--acd-navy-900)]">
                            {subs.active}
                            {subs.total !== subs.active && (
                                <span className="text-sm text-[color:var(--acd-muted)] font-normal ml-2">
                                    /{subs.total}
                                </span>
                            )}
                        </div>
                        <div className="text-xs text-[color:var(--acd-muted)] uppercase tracking-widest">
                            Subscritores newsletter
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-[var(--acd-navy-900)]">
                        Artigos publicados
                    </h1>
                    <p className="text-sm text-[color:var(--acd-muted)]">
                        {articles.length} artigo{articles.length === 1 ? "" : "s"} no total
                    </p>
                </div>
                <div className="flex gap-3">
                    <div className="relative">
                        <Search
                            size={16}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        />
                        <input
                            className="acd-input pl-10 min-w-[240px]"
                            placeholder="Pesquisar..."
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            data-testid="admin-search"
                        />
                    </div>
                    <Link
                        to="/admin/artigos/novo"
                        className="acd-btn-gradient"
                        data-testid="admin-new-article-btn"
                    >
                        <PlusCircle size={16} /> Novo artigo
                    </Link>
                </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-[color:var(--acd-muted)]">
                        A carregar...
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-8 text-center text-[color:var(--acd-muted)]">
                        Nenhum artigo encontrado
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-[var(--acd-bg-soft)] text-left text-xs uppercase tracking-widest text-[color:var(--acd-muted)]">
                                <tr>
                                    <th className="px-6 py-3">Título</th>
                                    <th className="px-6 py-3">Categoria</th>
                                    <th className="px-6 py-3">Autor</th>
                                    <th className="px-6 py-3">Data</th>
                                    <th className="px-6 py-3 text-right">
                                        Ações
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((a) => (
                                    <tr
                                        key={a.slug}
                                        className="border-t border-gray-100 hover:bg-gray-50 transition"
                                        data-testid={`admin-row-${a.slug.substring(0, 20)}`}
                                    >
                                        <td className="px-6 py-4">
                                            <div className="font-semibold text-[var(--acd-navy-900)] leading-tight">
                                                {a.title}
                                            </div>
                                            <div className="text-xs text-[color:var(--acd-muted)] mt-1">
                                                /{a.slug}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-[color:var(--acd-muted)]">
                                            {a.category}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-[color:var(--acd-muted)]">
                                            {a.author}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-[color:var(--acd-muted)] whitespace-nowrap">
                                            {a.date_display}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2 justify-end">
                                                <a
                                                    href={`/artigos/${a.slug}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="w-8 h-8 flex items-center justify-center rounded-lg text-[color:var(--acd-muted)] hover:bg-gray-100 hover:text-[var(--acd-navy-900)] transition"
                                                    title="Ver no site"
                                                >
                                                    <ExternalLink size={14} />
                                                </a>
                                                <Link
                                                    to={`/admin/artigos/${a.slug}/editar`}
                                                    className="w-8 h-8 flex items-center justify-center rounded-lg text-[var(--acd-blue-600)] hover:bg-blue-50 transition"
                                                    title="Editar"
                                                    data-testid={`admin-edit-${a.slug.substring(0, 20)}`}
                                                >
                                                    <Edit size={14} />
                                                </Link>
                                                <button
                                                    onClick={() =>
                                                        handleDelete(
                                                            a.slug,
                                                            a.title,
                                                        )
                                                    }
                                                    className="w-8 h-8 flex items-center justify-center rounded-lg text-red-600 hover:bg-red-50 transition"
                                                    title="Eliminar"
                                                    data-testid={`admin-delete-${a.slug.substring(0, 20)}`}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminDashboard;
